import * as React from 'react';
import { IgcSelectComponent as Component } from 'igniteui-webcomponents';
import type { IgcSelectComponentEventMap } from 'igniteui-webcomponents';
import { type EventName, createComponent } from '../react-props.js';

/**
 * Represents a control that provides a menu of options.
 * @slot  - Renders the list of select items.
 * @slot prefix - Renders content before the input.
 * @slot suffix - Renders content after input.
 * @slot header - Renders a container before the list of options.
 * @slot footer - Renders a container after the list of options.
 * @slot helper-text - Renders content below the input.
 * @slot toggle-icon - Renders content inside the suffix container.
 * @slot toggle-icon-expanded - Renders content for the toggle icon when the component is in open state.
 * @slot value-missing - Renders content when the required validation fails.
 * @slot custom-error - Renders content when setCustomValidity(message) is set.
 * @slot invalid - Renders content when the component is in invalid state (validity.valid = false).
 * @fires igcChange - Emitted when the control's checked state changes.
 * @fires igcOpening - Emitted just before the list of options is opened.
 * @fires igcOpened - Emitted after the list of options is opened.
 * @fires igcClosing - Emitter just before the list of options is closed.
 * @fires igcClosed - Emitted after the list of options is closed.
 * @csspart list - The list wrapping container for the items of the igc-select.
 * @csspart input - The encapsulated igc-input of the igc-select.
 * @csspart label - The encapsulated text label of the igc-select.
 * @csspart prefix - The prefix wrapper of the input of the igc-select.
 * @csspart suffix - The suffix wrapper of the input of the igc-select.
 * @csspart toggle-icon - The toggle icon wrapper of the igc-select.
 * @csspart helper-text - The helper text wrapper of the igc-select.
 * @class
 */
export const IgrSelect = /* @__PURE__ */ createComponent({
  tagName: 'igc-select',
  displayName: 'IgrSelect',
  elementClass: Component,
  react: React,
  events: {
    onChange: 'igcChange' as EventName<IgcSelectComponentEventMap['igcChange']>,
    onOpening: 'igcOpening' as EventName<
      IgcSelectComponentEventMap['igcOpening']
    >,
    onOpened: 'igcOpened' as EventName<IgcSelectComponentEventMap['igcOpened']>,
    onClosing: 'igcClosing' as EventName<
      IgcSelectComponentEventMap['igcClosing']
    >,
    onClosed: 'igcClosed' as EventName<IgcSelectComponentEventMap['igcClosed']>,
  },
});

export type IgrSelect = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrSelectModule = Component;
